/**
 * The words for a promo's terms, a gift card's credit and a redemption's state — one
 * wording for every plane that shows them (signup, Billing, the super-admin screen).
 *
 * Every function takes the caller's translator bound to "common" (where the keys live,
 * i18n/dictionaries/common/platformOffers.js) and the UI language; none calls a hook.
 *
 * The promise a promo makes is counted in PAID invoices (contract §5.0): a promo redeemed
 * during the trial is attached only when the trial ends, so "for 3 months" means three
 * discounted paid invoices, never "three months from today". The duration sentences say
 * "paid" for that reason.
 */
import { formatMoneyIntl, intlLocaleFor } from "../lib/formatMoney";

/** 2000 -> "20%" (en) / "20 %" (fr). Basis points are an integer; the display is not. */
export const formatPercentBp = (bp, lang) =>
  new Intl.NumberFormat(intlLocaleFor(lang), { style: "percent", maximumFractionDigits: 2 })
    .format(bp / 10000);

/**
 * "20% off" / "$10.00 off" for anything carrying the promo terms: a PromoOut (§4.1) or a
 * promo preview (§5.1). An amount promo's currency is the code's own.
 */
export function discountText(terms, t, lang) {
  return terms.discount_type === "percent"
    ? t("platformOffers.offer.percentOff", { percent: formatPercentBp(terms.percent_bp, lang) })
    : t("platformOffers.offer.amountOff", { amount: formatMoneyIntl(terms.amount_cents, terms.currency, lang) });
}

/** "on the first paid invoice" / "for 3 paid months" / "on every paid invoice". */
export function durationText(terms, t) {
  if (terms.duration === "once") return t("platformOffers.duration.once");
  if (terms.duration === "forever") return t("platformOffers.duration.forever");
  return terms.duration_in_months === 1
    ? t("platformOffers.duration.repeatingOne")
    : t("platformOffers.duration.repeating", { months: terms.duration_in_months });
}

/** One line for an applied preview: "20% off for 3 paid months" / "$50.00 credit". */
export function offerSummary(preview, t, lang) {
  if (preview.kind === "giftcard") {
    return t("platformOffers.offer.credit", { amount: formatMoneyIntl(preview.credit_cents, preview.currency, lang) });
  }
  return `${discountText(preview, t, lang)} ${durationText(preview, t)}`;
}

/** "$49.00 → $39.20 per invoice", from a promo preview's server-computed amounts. */
export function perInvoiceText(preview, t, lang) {
  return t("platformOffers.offer.perInvoice", {
    from: formatMoneyIntl(preview.plan_amount_cents, preview.currency, lang),
    to: formatMoneyIntl(preview.discounted_amount_cents, preview.currency, lang),
  });
}

// ── redemption vocabulary (§3.3) ─────────────────────────────────────────────

/** Every §3.3 status (§5.0 added `scheduled`). */
export const REDEMPTION_STATUS_KEYS = Object.freeze({
  held: "platformOffers.redemption.status.held",
  scheduled: "platformOffers.redemption.status.scheduled",
  applied: "platformOffers.redemption.status.applied",
  released: "platformOffers.redemption.status.released",
});

/** Every §3.3 `release_reason`. */
export const RELEASE_REASON_KEYS = Object.freeze({
  checkout_expired: "platformOffers.redemption.reason.checkout_expired",
  stripe_failed: "platformOffers.redemption.reason.stripe_failed",
  sweep: "platformOffers.redemption.reason.sweep",
  superseded: "platformOffers.redemption.reason.superseded",
  subscription_ended: "platformOffers.redemption.reason.subscription_ended",
  request_failed: "platformOffers.redemption.reason.request_failed",
  practice_deleted: "platformOffers.redemption.reason.practice_deleted",
});

/** Every §3.3 surface. */
export const SURFACE_KEYS = Object.freeze({
  signup: "platformOffers.redemption.surface.signup",
  plan_change: "platformOffers.redemption.surface.plan_change",
  billing_redeem: "platformOffers.redemption.surface.billing_redeem",
});

/** Badge palette per status, shared by the Billing list and the super-admin drawer. */
export const REDEMPTION_STATUS_TONES = Object.freeze({
  held: "bg-amber-100 text-amber-700",
  scheduled: "bg-sky-100 text-sky-700",
  applied: "bg-emerald-100 text-emerald-700",
  released: "bg-slate-100 text-slate-500",
});

/**
 * What a redemption was worth, in words: a gift card's credit, or the promo's code (a
 * RedemptionOut does not carry the promo's terms — only the code that was used).
 */
export const redemptionValueText = (r, t, lang) =>
  r.kind === "giftcard"
    ? t("platformOffers.offer.credit", { amount: formatMoneyIntl(r.amount_cents, r.currency, lang) })
    : t("platformOffers.redemption.kind.promo");
